import React, { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";

/**
 * Free-text city/town autocomplete backed by Photon (OpenStreetMap).
 *
 * - No API key. Public CORS-enabled endpoint.
 * - We only request `layer=city` so the dropdown returns cities, towns,
 *   and villages, not random POIs or street addresses.
 * - The selected place is returned to the parent as a structured object:
 *     { display, city, state, country, country_code }.
 * - The plain `<input>` value is only used as a search query, it does
 *   NOT count as a valid submission until the user clicks one of the
 *   suggestions. Clearing or editing the input after selecting resets
 *   the structured value so the parent form re-blocks submit.
 */
export default function LocationAutocomplete({
  value,
  onSelect,
  testIdPrefix = "location",
  placeholder = "Start typing your city…",
  className = "",
}) {
  const [query, setQuery] = useState(value?.display || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);
  const lastSelectedDisplayRef = useRef(value?.display || "");

  // Keep the input in sync when the parent clears/sets the value.
  useEffect(() => {
    setQuery(value?.display || "");
    lastSelectedDisplayRef.current = value?.display || "";
  }, [value?.display]);

  // Close the dropdown on outside click.
  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Debounced fetch.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = query.trim();
    // Don't fetch for very short queries or when the input still matches
    // a selection the user already picked.
    if (trimmed.length < 2 || trimmed === lastSelectedDisplayRef.current) {
      setResults([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          q: trimmed,
          limit: "6",
          layer: "city",
        });
        const r = await fetch(`https://photon.komoot.io/api/?${params.toString()}`);
        if (!r.ok) throw new Error(`photon ${r.status}`);
        const data = await r.json();
        const places = (data?.features || [])
          .map((f) => {
            const p = f.properties || {};
            const cityName = p.name || p.city || "";
            if (!cityName) return null;
            const display = [cityName, p.state, p.country].filter(Boolean).join(", ");
            const subtitle = [p.state, p.country].filter(Boolean).join(", ");
            return {
              key: `${f.geometry?.coordinates?.join(",") || ""}-${display}`,
              display,
              subtitle,
              city: cityName,
              state: p.state || "",
              country: p.country || "",
              country_code: (p.countrycode || "").toUpperCase(),
            };
          })
          .filter(Boolean);
        setResults(places);
        setOpen(true);
        setActiveIndex(places.length ? 0 : -1);
      } catch (e) {
        setResults([]);
        setOpen(false);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const choose = (place) => {
    setQuery(place.display);
    lastSelectedDisplayRef.current = place.display;
    setResults([]);
    setOpen(false);
    setActiveIndex(-1);
    onSelect && onSelect(place);
  };

  const onKeyDown = (e) => {
    if (!open || !results.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      if (activeIndex >= 0) {
        e.preventDefault();
        choose(results[activeIndex]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <Input
        type="text"
        value={query}
        placeholder={placeholder}
        data-testid={`${testIdPrefix}-input`}
        onChange={(e) => {
          setQuery(e.target.value);
          // If user edits after selecting, invalidate the structured value
          if (lastSelectedDisplayRef.current && e.target.value !== lastSelectedDisplayRef.current) {
            lastSelectedDisplayRef.current = "";
            onSelect && onSelect(null);
          }
        }}
        onFocus={() => {
          if (results.length) setOpen(true);
        }}
        onKeyDown={onKeyDown}
        autoComplete="off"
        className="h-11 bg-white/[0.06] border-white/15 text-white placeholder:text-white/40"
      />
      {open && (results.length > 0 || loading) && (
        <ul
          data-testid={`${testIdPrefix}-results`}
          className="absolute z-50 left-0 right-0 mt-1 rounded-md border border-white/15 overflow-hidden shadow-2xl"
          style={{ backgroundColor: "#0C0826" }}
        >
          {loading && !results.length && (
            <li className="px-3 py-2 text-xs" style={{ color: "rgba(245,239,227,0.55)" }}>
              Searching…
            </li>
          )}
          {results.map((place, idx) => (
            <li key={place.key}>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  choose(place);
                }}
                onMouseEnter={() => setActiveIndex(idx)}
                data-testid={`${testIdPrefix}-option-${idx}`}
                className="block w-full text-left px-3 py-2 text-sm transition-colors"
                style={{
                  color: idx === activeIndex ? "#F2D589" : "rgba(245,239,227,0.85)",
                  backgroundColor: idx === activeIndex ? "rgba(255,255,255,0.06)" : "transparent",
                }}
              >
                <span className="block">{place.city}</span>
                <span
                  className="block text-[11px]"
                  style={{ color: idx === activeIndex ? "rgba(232,196,138,0.7)" : "rgba(245,239,227,0.5)" }}
                >
                  {place.subtitle}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
